import React from 'react';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import { certifications } from '../../data/constants';

// Certifications Container
const CertificationsContainer = styled.div`
  width: 100%;
  padding: 60px 0 80px 0;
  display: flex;
  justify-content: center;
  background: transparent;
  scroll-margin-top: 100px;
  
  @media (max-width: 960px) {
    padding: 40px 0 60px 0;
  }
`;

// Certifications Wrapper
const CertificationsWrapper = styled.div`
  width: 100%;
  max-width: 1350px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: center;
  padding: 0 16px;
`;

// Title
const Title = styled.h1`
  font-family: 'Mosca Laroke', 'Playfair Display', 'Bodoni Moda', 'Cinzel', serif;
  font-size: 76px;
  font-weight: 700;
  text-transform: uppercase;
  text-align: center;
  line-height: 1;
  color: ${({ theme }) => theme.primary};
  margin-bottom: 32px;
  letter-spacing: 0.5px;

  @media (max-width: 1400px) { font-size: 64px; }
  @media (max-width: 1200px) { font-size: 52px; }
  @media (max-width: 768px)  { font-size: 44px; }
`;

// Grid for Certifications
const CertificationsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;
  width: 100%;
  max-width: 1200px;
  margin-top: 20px;
  
  @media (max-width: 960px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
  }
  
  @media (max-width: 640px) {
    grid-template-columns: repeat(1, 1fr);
  }
`;

// Individual Certification Card
const CertificationCard = styled.a`
  background: ${({ theme }) => theme.card};
  border: 0.1px solid #854CE6;
  box-shadow: rgba(23, 92, 230, 0.15) 0px 4px 24px;
  border-radius: 16px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  transition: all 0.3s ease-in-out;
  text-decoration: none;
  color: ${({ theme }) => theme.text_primary};
  cursor: pointer;
  height: 100%;
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: rgba(23, 92, 230, 0.3) 0px 8px 32px;
  }
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
`;

const CardTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: ${({ theme }) => theme.text_primary};
  line-height: 1.4;
  flex: 1;
`;

const PlatformBadge = styled.div`
  background: ${({ platform }) =>
    platform === 'coursera' ? 'linear-gradient(135deg, #0056D2, #0073E6)' :
      platform === 'linkedin' ? 'linear-gradient(135deg, #0077B5, #00A0DC)' :
        platform === 'hackerrank' ? 'linear-gradient(135deg, #00EA64, #00C853)' :
          'linear-gradient(135deg, #854CE6, #9D5CE8)'
  };
  color: white;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  white-space: nowrap;
`;

const IssuerName = styled.div`
  font-size: 14px;
  color: ${({ theme }) => theme.text_secondary};
  margin-bottom: 8px;
`;

const DateText = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.text_secondary};
  margin-bottom: 12px;
`;

const SkillsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: auto;
`;

const SkillBadge = styled.span`
  background: rgba(133, 76, 230, 0.1);
  color: ${({ theme }) => theme.primary};
  padding: 4px 10px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 500;
  border: 1px solid rgba(133, 76, 230, 0.3);
`;

const ViewButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px 16px;
  background: linear-gradient(225deg, hsla(271, 100%, 50%, 1) 0%, hsla(294, 100%, 50%, 1) 100%);
  color: white;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  margin-top: 12px;
  transition: all 0.3s ease-in-out;
  
  &:hover {
    transform: scale(1.02);
    box-shadow: rgba(133, 76, 230, 0.4) 0px 4px 16px;
  }
`;

function CertificationSection() {
  return (
    <CertificationsContainer id="certifications">
      <CertificationsWrapper
        as={motion.div}
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8, ease: "easeOut" }}
      >
        <Title>CERTIFICATIONS</Title>

        <CertificationsGrid>
          {certifications.map((cert) => (
            <CertificationCard
              key={cert.id}
              href={cert.link}
              target="_blank"
              rel="noopener noreferrer"
            >
              <CardHeader>
                <CardTitle>{cert.title}</CardTitle>
                <PlatformBadge platform={cert.platform}>
                  {cert.platform}
                </PlatformBadge>
              </CardHeader>

              <IssuerName>{cert.issuer}</IssuerName>
              <DateText>Issued: {cert.date}</DateText>

              <SkillsContainer>
                {cert.skills.map((skill, index) => (
                  <SkillBadge key={index}>{skill}</SkillBadge>
                ))}
              </SkillsContainer>

              <ViewButton>
                View Certificate →
              </ViewButton>
            </CertificationCard>
          ))}
        </CertificationsGrid>
      </CertificationsWrapper>
    </CertificationsContainer>
  );
}

export default CertificationSection;